import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Eye, Edit, Trash2, LayoutGrid, List, ArrowLeft, Filter, X, Calendar, Users, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { getPlainTextPreview, formatDateSafe, isDeadlineOverdue } from '../utils/textUtils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TaskManagement = ({ user }) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0
  });
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    assignedTo: [],
    hideCommonTasks: false
  });
  const [activeFilters, setActiveFilters] = useState(false);
  const [quickFilter, setQuickFilter] = useState('all'); // 'all', 'assigned_to_me', 'assigned_by_me'

  useEffect(() => {
    fetchTasks();
    fetchUsers();
  }, [pagination.page]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/tasks`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: pagination.page,
          page_size: pagination.pageSize
        }
      });
      setTasks(response.data.tasks);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        totalPages: response.data.total_pages
      }));
    } catch (error) {
      toast.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const handlePickTask = async (taskId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/tasks/${taskId}/pick`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task picked successfully!');
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchTasks(); // Refresh tasks
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pick task');
    }
  };

  const handleUnpickTask = async (taskId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/tasks/${taskId}/unpick`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task unpicked successfully!');
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchTasks(); // Refresh tasks
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unpick task');
    }
  };

  const handleDeleteTask = async (task) => {
    // Check if task has assignments
    const hasAssignedUser = task.assigned_to && Array.isArray(task.assigned_to) && task.assigned_to.length > 0;
    const hasPickedUsers = task.picked_by && task.picked_by.length > 0;
    
    let confirmMessage = 'Are you sure you want to delete this task?';
    
    if (hasAssignedUser || hasPickedUsers) {
      confirmMessage = '⚠️ This task has assignments that will be removed:\n\n';
      if (hasAssignedUser) {
        const assignedUserNames = task.assigned_to.map(id => getUserName(id)).join(', ');
        confirmMessage += `• Assigned to: ${assignedUserNames}\n`;
      }
      if (hasPickedUsers) {
        confirmMessage += `• Picked by ${task.picked_by.length} user(s)\n`;
      }
      confirmMessage += '\nAll assignments will be removed. Do you want to proceed?';
    }
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/tasks/${task.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task deleted successfully!');
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    }
  };

  const handleEditTask = (taskId) => {
    navigate(`/dashboard/tasks/edit/${taskId}`);
  };

  const getUserName = (userId) => {
    const u = users.find(user => user.id === userId);
    return u ? u.full_name : 'Unknown';
  };

  const getPriorityColor = (priority) => {
    // Priority is now a number 1-10
    if (priority >= 8) return '#f44336'; // High priority (8-10)
    if (priority >= 5) return '#ff9800'; // Medium priority (5-7)
    return '#4caf50'; // Low priority (1-4)
  };

  const getPriorityLabel = (priority) => {
    // Display the numeric priority
    return `${priority}/10`;
  };

  const getDeadlineStatus = (deadline, status) => {
    if (!deadline) return null;
    
    // Parse date safely without timezone issues
    const datePart = deadline.split('T')[0];
    const [year, month, day] = datePart.split('-');
    const deadlineDate = new Date(year, month - 1, day);
    
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24));
    
    // If task is completed
    if (status === 'completed') {
      return {
        label: 'Completed',
        color: '#4caf50',
        icon: '✓'
      };
    }
    
    // If overdue
    if (diffDays < 0) {
      return {
        label: `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'}`,
        color: '#f44336',
        icon: '⚠️'
      };
    }
    
    // If due today
    if (diffDays === 0) {
      return {
        label: 'Due Today',
        color: '#ff9800',
        icon: '🔔'
      };
    }
    
    // If due within 2 days
    if (diffDays <= 2) {
      return {
        label: `Due in ${diffDays} day${diffDays === 1 ? '' : 's'}`,
        color: '#ff9800',
        icon: '⏰'
      };
    }
    
    // On time
    return {
      label: `Due in ${diffDays} days`,
      color: '#4caf50',
      icon: '📅'
    };
  };

  const formatDeadlineDate = (deadline) => {
    if (!deadline) return null;
    // Parse date safely without timezone issues
    const datePart = deadline.split('T')[0];
    const [year, month, day] = datePart.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Apply filters to tasks
  const applyFilters = useCallback((taskList) => {
    let filtered = [...taskList];
    
    // Apply quick filter first
    if (quickFilter === 'assigned_to_me') {
      filtered = filtered.filter(task => 
        (task.assigned_to && Array.isArray(task.assigned_to) && task.assigned_to.includes(user.id)) ||
        (task.picked_by && task.picked_by.includes(user.id))
      );
    } else if (quickFilter === 'assigned_by_me') {
      filtered = filtered.filter(task => task.created_by === user.id);
    }
    
    // Filter by date (deadline)
    if (filters.dateFrom) {
      filtered = filtered.filter(task => {
        if (!task.deadline) return false;
        const taskDate = task.deadline.split('T')[0];
        return taskDate >= filters.dateFrom;
      });
    }
    if (filters.dateTo) {
      filtered = filtered.filter(task => {
        if (!task.deadline) return false;
        const taskDate = task.deadline.split('T')[0];
        return taskDate <= filters.dateTo;
      });
    }
    
    // Filter by assigned users
    if (filters.assignedTo.length > 0) {
      filtered = filtered.filter(task => {
        if (!task.assigned_to || task.assigned_to.length === 0) {
          // For common tasks, check if anyone from the filter picked it
          return task.picked_by && task.picked_by.some(id => filters.assignedTo.includes(id));
        }
        return task.assigned_to.some(id => filters.assignedTo.includes(id));
      });
    }
    
    // Hide common tasks checkbox
    if (filters.hideCommonTasks) {
      filtered = filtered.filter(task => {
        const isCommonTask = !task.assigned_to || task.assigned_to.length === 0;
        if (!isCommonTask) return true;
        // Show common tasks only if someone picked them
        return task.picked_by && task.picked_by.length > 0;
      });
    }
    
    return filtered;
  }, [filters, quickFilter, user.id]);

  // Check if any filters are active
  const hasActiveFilters = filters.dateFrom || filters.dateTo || filters.assignedTo.length > 0 || filters.hideCommonTasks || quickFilter !== 'all';

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      assignedTo: [],
      hideCommonTasks: false
    });
    setQuickFilter('all');
    setActiveFilters(false);
  };

  // Get filtered tasks - always apply filters now (including quick filter)
  const filteredTasks = applyFilters(tasks);

  // Separate tasks into categories (use filtered tasks)
  const commonTasks = filteredTasks.filter(task => !task.assigned_to || (Array.isArray(task.assigned_to) && task.assigned_to.length === 0));
  const myTasks = filteredTasks.filter(task => (task.assigned_to && Array.isArray(task.assigned_to) && task.assigned_to.includes(user.id)) || (task.picked_by && task.picked_by.includes(user.id)));
  const teamTasks = filteredTasks.filter(task => task.assigned_to && Array.isArray(task.assigned_to) && task.assigned_to.length > 0 && !task.assigned_to.includes(user.id) && (!task.picked_by || !task.picked_by.includes(user.id)));

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, pagination.page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px',
        marginTop: '24px',
        padding: '20px'
      }}>
        <button
          onClick={() => handlePageChange(pagination.page - 1)}
          disabled={pagination.page === 1}
          style={{
            padding: '8px 16px',
            border: '1px solid #7077FE',
            borderRadius: '6px',
            background: pagination.page === 1 ? '#f5f5f5' : 'white',
            color: pagination.page === 1 ? '#999' : '#7077FE',
            cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
            fontWeight: '600',
            fontSize: '14px'
          }}
        >
          Previous
        </button>

        {startPage > 1 && (
          <>
            <button
              onClick={() => handlePageChange(1)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                background: 'white',
                color: '#333',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              1
            </button>
            {startPage > 2 && <span style={{ color: '#999' }}>...</span>}
          </>
        )}

        {pages.map(page => (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            style={{
              padding: '8px 12px',
              border: `1px solid ${page === pagination.page ? '#7077FE' : '#ddd'}`,
              borderRadius: '6px',
              background: page === pagination.page ? '#7077FE' : 'white',
              color: page === pagination.page ? 'white' : '#333',
              cursor: 'pointer',
              fontWeight: page === pagination.page ? '600' : '400',
              fontSize: '14px'
            }}
          >
            {page}
          </button>
        ))}

        {endPage < pagination.totalPages && (
          <>
            {endPage < pagination.totalPages - 1 && <span style={{ color: '#999' }}>...</span>}
            <button
              onClick={() => handlePageChange(pagination.totalPages)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                background: 'white',
                color: '#333',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {pagination.totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => handlePageChange(pagination.page + 1)}
          disabled={pagination.page === pagination.totalPages}
          style={{
            padding: '8px 16px',
            border: '1px solid #7077FE',
            borderRadius: '6px',
            background: pagination.page === pagination.totalPages ? '#f5f5f5' : 'white',
            color: pagination.page === pagination.totalPages ? '#999' : '#7077FE',
            cursor: pagination.page === pagination.totalPages ? 'not-allowed' : 'pointer',
            fontWeight: '600',
            fontSize: '14px'
          }}
        >
          Next
        </button>

        <span style={{ marginLeft: '16px', color: '#666', fontSize: '14px' }}>
          Page {pagination.page} of {pagination.totalPages} ({pagination.total} total tasks)
        </span>
      </div>
    );
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="management-page" data-testid="task-management">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/dashboard/cness-tasks')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              transition: 'all 0.2s'
            }}
          >
            <ArrowLeft size={18} />
            Back
          </button>
          <h2 style={{ margin: 0 }}>Task Management</h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Filter Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              background: hasActiveFilters ? '#7077FE20' : 'var(--bg-secondary)',
              border: hasActiveFilters ? '2px solid #7077FE' : '1px solid var(--border-color)',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              color: hasActiveFilters ? '#7077FE' : 'var(--text-primary)',
              transition: 'all 0.2s'
            }}
            data-testid="filter-toggle-btn"
          >
            <Filter size={18} />
            Filters
            {hasActiveFilters && (
              <span style={{
                background: '#7077FE',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '11px',
                fontWeight: '700'
              }}>
                Active
              </span>
            )}
          </button>

          {/* View Toggle */}
          <div style={{
            display: 'flex',
            background: 'var(--bg-secondary)',
            borderRadius: '8px',
            padding: '4px',
            border: '1px solid var(--border-color)'
          }}>
            <button
              onClick={() => setViewMode('grid')}
              style={{
                padding: '8px 12px',
                background: viewMode === 'grid' ? 'var(--color-primary)' : 'transparent',
                color: viewMode === 'grid' ? 'white' : 'var(--text-secondary)',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
              title="Grid View"
            >
              <LayoutGrid size={16} />
              Grid
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '8px 12px',
                background: viewMode === 'list' ? 'var(--color-primary)' : 'transparent',
                color: viewMode === 'list' ? 'white' : 'var(--text-secondary)',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
              title="List View"
            >
              <List size={16} />
              List
            </button>
          </div>
          {user.role !== 'team_member' && (
            <button 
              className="gradient-btn" 
              onClick={() => navigate('/dashboard/tasks/new')}
              data-testid="add-task-btn"
            >
              <Plus size={18} /> Add Task
            </button>
          )}
        </div>
      </div>

      {/* Quick Filter Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '20px',
        padding: '4px',
        background: 'var(--bg-secondary)',
        borderRadius: '12px',
        width: 'fit-content',
        border: '1px solid var(--border-color)'
      }}>
        <button
          onClick={() => setQuickFilter('all')}
          style={{
            padding: '10px 20px',
            background: quickFilter === 'all' ? 'var(--color-primary)' : 'transparent',
            color: quickFilter === 'all' ? 'white' : 'var(--text-secondary)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '13px',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
          data-testid="quick-filter-all"
        >
          All Tasks
          <span style={{
            background: quickFilter === 'all' ? 'rgba(255,255,255,0.2)' : 'var(--bg-primary)',
            padding: '2px 8px',
            borderRadius: '10px',
            fontSize: '11px'
          }}>{tasks.length}</span>
        </button>
        <button
          onClick={() => setQuickFilter('assigned_to_me')}
          style={{
            padding: '10px 20px',
            background: quickFilter === 'assigned_to_me' ? '#10B981' : 'transparent',
            color: quickFilter === 'assigned_to_me' ? 'white' : 'var(--text-secondary)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '13px',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
          data-testid="quick-filter-assigned-to-me"
        >
          Assigned to Me
          <span style={{
            background: quickFilter === 'assigned_to_me' ? 'rgba(255,255,255,0.2)' : 'var(--bg-primary)',
            padding: '2px 8px',
            borderRadius: '10px',
            fontSize: '11px'
          }}>{tasks.filter(t => (t.assigned_to && t.assigned_to.includes(user.id)) || (t.picked_by && t.picked_by.includes(user.id))).length}</span>
        </button>
        <button
          onClick={() => setQuickFilter('assigned_by_me')}
          style={{
            padding: '10px 20px',
            background: quickFilter === 'assigned_by_me' ? '#F59E0B' : 'transparent',
            color: quickFilter === 'assigned_by_me' ? 'white' : 'var(--text-secondary)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '13px',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
          data-testid="quick-filter-assigned-by-me"
        >
          Assigned by Me
          <span style={{
            background: quickFilter === 'assigned_by_me' ? 'rgba(255,255,255,0.2)' : 'var(--bg-primary)',
            padding: '2px 8px',
            borderRadius: '10px',
            fontSize: '11px'
          }}>{tasks.filter(t => t.created_by === user.id).length}</span>
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div style={{
          padding: '20px',
          background: 'var(--bg-secondary)',
          borderRadius: '12px',
          border: '1px solid var(--border-color)',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Filter size={18} />
              Filter Tasks
            </h3>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  background: '#EF444420',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#EF4444',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '13px'
                }}
              >
                <X size={14} />
                Clear All
              </button>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            {/* Date Range Filter */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '13px', color: 'var(--text-secondary)' }}>
                <Calendar size={14} style={{ display: 'inline', marginRight: '6px' }} />
                Deadline From
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontSize: '14px'
                }}
                data-testid="filter-date-from"
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '13px', color: 'var(--text-secondary)' }}>
                <Calendar size={14} style={{ display: 'inline', marginRight: '6px' }} />
                Deadline To
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontSize: '14px'
                }}
                data-testid="filter-date-to"
              />
            </div>

            {/* Assigned To Filter */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '13px', color: 'var(--text-secondary)' }}>
                <Users size={14} style={{ display: 'inline', marginRight: '6px' }} />
                Assigned To
              </label>
              <select
                multiple
                value={filters.assignedTo}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, option => option.value);
                  setFilters({ ...filters, assignedTo: selected });
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                  height: '100px'
                }}
                data-testid="filter-assigned-to"
              >
                {users.filter(u => u.full_name).map(u => (
                  <option key={u.id} value={u.id}>
                    {u.full_name.replace(/\s+\d+$/, '').trim()}
                  </option>
                ))}
              </select>
              <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: 'var(--text-tertiary)' }}>
                Hold Ctrl/Cmd to select multiple
              </p>
            </div>

            {/* Hide Common Tasks */}
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px',
                cursor: 'pointer',
                padding: '12px',
                background: filters.hideCommonTasks ? '#F59E0B20' : 'var(--bg-primary)',
                borderRadius: '8px',
                border: filters.hideCommonTasks ? '2px solid #F59E0B' : '1px solid var(--border-color)'
              }}>
                <input
                  type="checkbox"
                  checked={filters.hideCommonTasks}
                  onChange={(e) => setFilters({ ...filters, hideCommonTasks: e.target.checked })}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  data-testid="filter-hide-common"
                />
                <div>
                  <span style={{ fontWeight: '500', fontSize: '14px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <EyeOff size={16} />
                    Hide Common Tasks
                  </span>
                  <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: 'var(--text-tertiary)' }}>
                    Show only unpicked common tasks
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Selected filters display */}
          {filters.assignedTo.length > 0 && (
            <div style={{ marginTop: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)', alignSelf: 'center' }}>Filtering by:</span>
              {filters.assignedTo.map(userId => {
                const filteredUser = users.find(u => u.id === userId);
                if (!filteredUser) return null;
                return (
                  <span key={userId} style={{
                    padding: '4px 10px',
                    background: '#7077FE20',
                    color: '#7077FE',
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    {filteredUser.full_name.replace(/\s+\d+$/, '').trim()}
                    <button
                      onClick={() => setFilters({ ...filters, assignedTo: filters.assignedTo.filter(id => id !== userId) })}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', color: '#7077FE' }}
                    >
                      <X size={12} />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Filter Summary */}
      {hasActiveFilters && !showFilters && (
        <div style={{
          padding: '12px 16px',
          background: '#7077FE10',
          borderRadius: '8px',
          border: '1px solid #7077FE30',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', color: '#7077FE', fontWeight: '500' }}>
              Filters active: Showing {filteredTasks.length} of {tasks.length} tasks
            </span>
            {quickFilter !== 'all' && (
              <span style={{
                padding: '4px 10px',
                background: quickFilter === 'assigned_to_me' ? '#10B98120' : '#F59E0B20',
                color: quickFilter === 'assigned_to_me' ? '#10B981' : '#F59E0B',
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: '500'
              }}>
                {quickFilter === 'assigned_to_me' ? 'Assigned to Me' : 'Assigned by Me'}
              </span>
            )}
          </div>
          <button
            onClick={clearFilters}
            style={{
              padding: '4px 12px',
              background: 'transparent',
              border: '1px solid #7077FE',
              borderRadius: '6px',
              color: '#7077FE',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '12px'
            }}
          >
            Clear
          </button>
        </div>
      )}
      {myTasks.length > 0 && (
        <>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            marginBottom: '16px',
            padding: '14px 20px',
            background: 'rgba(36, 22, 101, 0.1)',
            borderRadius: '10px',
            color: 'var(--text-primary)',
            border: '1px solid var(--color-primary)'
          }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--color-primary)' }}>📌 My Tasks ({myTasks.length})</h3>
          </div>
          <div className={viewMode === 'grid' ? 'content-grid' : 'content-list'} style={viewMode === 'list' ? { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '32px' } : { marginBottom: '32px' }}>
            {myTasks.map((task) => {
              const isPickedByUser = task.picked_by && task.picked_by.includes(user.id);
              return viewMode === 'list' ? (
                <div className="item-card card" key={task.id} data-testid={`task-card-${task.id}`} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '16px', 
                  padding: '14px 20px',
                  borderLeft: '4px solid var(--color-primary)'
                }}>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={{ 
                        margin: 0, fontSize: '15px', fontWeight: '600',
                        color: 'var(--text-primary)',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '250px'
                      }}>{task.title}</h3>
                      <p style={{ 
                        margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-tertiary)',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '300px'
                      }}>{getPlainTextPreview(task.description, 100)}</p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                      {isPickedByUser && <span style={{ fontSize: '11px', color: 'var(--color-success)', fontWeight: '600' }}>✓ Picked</span>}
                      <span style={{
                        padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                        backgroundColor: getPriorityColor(task.priority) + '20', color: getPriorityColor(task.priority)
                      }}>{getPriorityLabel(task.priority)}</span>
                      <span className={`status-badge ${task.status}`} style={{ fontSize: '11px', padding: '4px 10px' }}>{task.status}</span>
                      {task.deadline && (
                        <span style={{
                          padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                          backgroundColor: getDeadlineStatus(task.deadline, task.status).color + '20',
                          color: getDeadlineStatus(task.deadline, task.status).color
                        }}>{getDeadlineStatus(task.deadline, task.status).icon} {formatDeadlineDate(task.deadline)}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                    <button className="gradient-btn" onClick={() => navigate(`/dashboard/tasks/${task.id}`)} style={{ padding: '6px 12px', fontSize: '12px' }}>View</button>
                    {user.role !== 'team_member' && (
                      <>
                        <button onClick={() => handleEditTask(task.id)} title="Edit" className="icon-btn edit" style={{ padding: '8px 12px' }}><Edit size={16} /></button>
                        <button onClick={() => handleDeleteTask(task)} title="Delete" className="icon-btn delete" style={{ padding: '8px 12px' }}><Trash2 size={16} /></button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="item-card card" key={task.id} data-testid={`task-card-${task.id}`} style={{ borderLeft: '4px solid var(--color-primary)' }}>
                  <div className="item-header">
                    <h3 style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}>{task.title}</h3>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button className="gradient-btn" onClick={() => navigate(`/dashboard/tasks/${task.id}`)} style={{ padding: '8px 16px', fontSize: '14px' }}><Eye size={16} /> View</button>
                      {user.role !== 'team_member' && (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button onClick={() => handleEditTask(task.id)} title="Edit" className="icon-btn edit" style={{ padding: '8px 12px' }}><Edit size={16} /></button>
                          <button onClick={() => handleDeleteTask(task)} title="Delete" className="icon-btn delete" style={{ padding: '8px 12px' }}><Trash2 size={16} /></button>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="item-description" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{getPlainTextPreview(task.description, 150)}</p>
                  <div className="item-footer">
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {isPickedByUser && <span style={{ fontSize: '12px', color: 'var(--color-success)', fontWeight: '600' }}>✓ Picked by you</span>}
                      <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', backgroundColor: getPriorityColor(task.priority) + '20', color: getPriorityColor(task.priority) }}>{getPriorityLabel(task.priority)}</span>
                    </div>
                    <span className={`status-badge ${task.status}`}>{task.status}</span>
                  </div>
                  {task.deadline && (
                    <div style={{ marginTop: '12px' }}>
                      <span style={{ padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', backgroundColor: getDeadlineStatus(task.deadline, task.status).color + '20', color: getDeadlineStatus(task.deadline, task.status).color }}>
                        {getDeadlineStatus(task.deadline, task.status).icon} {formatDeadlineDate(task.deadline)}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Common Tasks Section */}
      {commonTasks.length > 0 && (
        <>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            marginBottom: '16px',
            padding: '14px 20px',
            background: '#E7EDF9',
            borderRadius: '10px',
            color: 'var(--text-primary)'
          }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>🌟 Common Tasks (Available for Everyone)</h3>
          </div>
          <div className={viewMode === 'grid' ? 'content-grid' : 'content-list'} style={viewMode === 'list' ? { display: 'flex', flexDirection: 'column', gap: '12px' } : {}}>
            {commonTasks.map((task) => {
              const isPickedByUser = task.picked_by && task.picked_by.includes(user.id);
              const pickedCount = task.picked_by ? task.picked_by.length : 0;
              
              return viewMode === 'list' ? (
                /* LIST VIEW - Compact horizontal layout */
                <div className="item-card card" key={task.id} data-testid={`task-card-${task.id}`} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '16px', 
                  padding: '14px 20px'
                }}>
                  {/* Left Side - Task Details */}
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={{ 
                        margin: 0, 
                        fontSize: '15px', 
                        fontWeight: '600',
                        color: 'var(--text-primary)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '250px'
                      }}>
                        {task.title}
                      </h3>
                      <p style={{ 
                        margin: '4px 0 0 0', 
                        fontSize: '13px', 
                        color: 'var(--text-tertiary)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '300px'
                      }}>
                        {getPlainTextPreview(task.description, 100)}
                      </p>
                    </div>
                    
                    {/* Badges */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        backgroundColor: getPriorityColor(task.priority) + '20',
                        color: getPriorityColor(task.priority)
                      }}>
                        {getPriorityLabel(task.priority)}
                      </span>
                      <span className={`status-badge ${task.status}`} style={{ fontSize: '11px', padding: '4px 10px' }}>
                        {task.status}
                      </span>
                      {task.deadline && (
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600',
                          backgroundColor: getDeadlineStatus(task.deadline, task.status).color + '20',
                          color: getDeadlineStatus(task.deadline, task.status).color
                        }}>
                          {getDeadlineStatus(task.deadline, task.status).icon} {formatDeadlineDate(task.deadline)}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Right Side - Actions */}
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                    {isPickedByUser ? (
                      <button 
                        className="btn-outline" 
                        onClick={() => handleUnpickTask(task.id)}
                        style={{ padding: '6px 12px', fontSize: '12px', borderColor: '#d32f2f', color: '#d32f2f' }}
                      >
                        Unpick
                      </button>
                    ) : (
                      <button 
                        className="gradient-btn" 
                        onClick={() => handlePickTask(task.id)}
                        style={{ padding: '6px 12px', fontSize: '12px' }}
                      >
                        Pick
                      </button>
                    )}
                    <button 
                      className="btn-outline" 
                      onClick={() => navigate(`/dashboard/tasks/${task.id}`)}
                      style={{ padding: '6px 12px', fontSize: '12px' }}
                    >
                      View
                    </button>
                    {user.role !== 'team_member' && (
                      <>
                        <button 
                          onClick={() => handleEditTask(task.id)}
                          title="Edit"
                          className="icon-btn edit"
                          style={{ padding: '6px' }}
                        >
                          <Edit size={16} />
                        </button>
                        <button 
                          onClick={() => handleDeleteTask(task)}
                          title="Delete"
                          className="icon-btn delete"
                          style={{ padding: '6px' }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                /* GRID VIEW - Card layout */
                <div className="item-card card" key={task.id} data-testid={`task-card-${task.id}`}>
                  <div className="item-header">
                    <h3 style={{ 
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '200px'
                    }}>{task.title}</h3>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {isPickedByUser ? (
                        <button 
                          className="btn-outline" 
                          onClick={() => handleUnpickTask(task.id)}
                          style={{ padding: '8px 16px', fontSize: '14px', borderColor: '#d32f2f', color: '#d32f2f' }}
                        >
                          Unpick Task
                        </button>
                      ) : (
                        <button 
                          className="gradient-btn" 
                          onClick={() => handlePickTask(task.id)}
                          style={{ padding: '8px 16px', fontSize: '14px' }}
                        >
                          Pick Task
                        </button>
                      )}
                      <button 
                        className="btn-outline" 
                        onClick={() => navigate(`/dashboard/tasks/${task.id}`)}
                        style={{ padding: '8px 16px', fontSize: '14px' }}
                      >
                        <Eye size={16} /> View
                      </button>
                      {user.role !== 'team_member' && (
                        <div style={{ display: 'flex', gap: '6px', marginLeft: '4px' }}>
                          <button 
                            onClick={() => handleEditTask(task.id)}
                            title="Edit"
                            className="icon-btn edit"
                          >
                            <Edit size={16} />
                          </button>
                          <button 
                            onClick={() => handleDeleteTask(task)}
                            title="Delete"
                            className="icon-btn delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="item-description" style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>{getPlainTextPreview(task.description, 150)}</p>
                  <div className="item-footer">
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span className="assigned-to" style={{ color: '#7077FE', fontWeight: 'bold', fontSize: '13px' }}>
                        {isPickedByUser ? '✅ Picked' : '⭐ Available'}
                        {pickedCount > 0 && ` (${pickedCount})`}
                      </span>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: getPriorityColor(task.priority) + '20',
                        color: getPriorityColor(task.priority)
                      }}>
                        {getPriorityLabel(task.priority)}
                      </span>
                    </div>
                    <span className={`status-badge ${task.status}`}>{task.status}</span>
                  </div>
                  {task.deadline && (
                    <div style={{ marginTop: '12px' }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: getDeadlineStatus(task.deadline, task.status).color + '20',
                        color: getDeadlineStatus(task.deadline, task.status).color
                      }}>
                        {getDeadlineStatus(task.deadline, task.status).icon} {formatDeadlineDate(task.deadline)}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Team Tasks Section */}
      {teamTasks.length > 0 && (
        <>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            margin: '32px 0 16px 0',
            padding: '14px 20px',
            background: '#E7EDF9',
            borderRadius: '10px'
          }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>👥 Team Tasks</h3>
          </div>
          <div className={viewMode === 'grid' ? 'content-grid' : 'content-list'} style={viewMode === 'list' ? { display: 'flex', flexDirection: 'column', gap: '12px' } : {}}>
            {teamTasks.map((task) => (
              viewMode === 'list' ? (
                /* LIST VIEW - Compact horizontal layout */
                <div className="item-card card" key={task.id} data-testid={`task-card-${task.id}`} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '16px', 
                  padding: '14px 20px'
                }}>
                  {/* Left Side - Task Details */}
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={{ 
                        margin: 0, 
                        fontSize: '15px', 
                        fontWeight: '600',
                        color: 'var(--text-primary)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '250px'
                      }}>
                        {task.title}
                      </h3>
                      <p style={{ 
                        margin: '4px 0 0 0', 
                        fontSize: '13px', 
                        color: 'var(--text-tertiary)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '300px'
                      }}>
                        {getPlainTextPreview(task.description, 100)}
                      </p>
                    </div>
                    
                    {/* Badges */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        👤 {task.assigned_to && Array.isArray(task.assigned_to) && task.assigned_to.length > 0
                            ? (task.assigned_to.map(id => getUserName(id)).join(', ').length > 20 
                                ? task.assigned_to.map(id => getUserName(id)).join(', ').substring(0, 20) + '...' 
                                : task.assigned_to.map(id => getUserName(id)).join(', '))
                            : 'Unassigned'}
                      </span>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        backgroundColor: getPriorityColor(task.priority) + '20',
                        color: getPriorityColor(task.priority)
                      }}>
                        {getPriorityLabel(task.priority)}
                      </span>
                      <span className={`status-badge ${task.status}`} style={{ fontSize: '11px', padding: '4px 10px' }}>
                        {task.status}
                      </span>
                      {task.deadline && (
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600',
                          backgroundColor: getDeadlineStatus(task.deadline, task.status).color + '20',
                          color: getDeadlineStatus(task.deadline, task.status).color
                        }}>
                          {getDeadlineStatus(task.deadline, task.status).icon} {formatDeadlineDate(task.deadline)}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Right Side - Actions */}
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                    <button 
                      className="gradient-btn" 
                      onClick={() => navigate(`/dashboard/tasks/${task.id}`)}
                      style={{ padding: '6px 12px', fontSize: '12px' }}
                    >
                      View
                    </button>
                    {user.role !== 'team_member' && (
                      <>
                        <button 
                          onClick={() => handleEditTask(task.id)}
                          title="Edit"
                          className="icon-btn edit"
                          style={{ padding: '6px' }}
                        >
                          <Edit size={16} />
                        </button>
                        <button 
                          onClick={() => handleDeleteTask(task)}
                          title="Delete"
                          className="icon-btn delete"
                          style={{ padding: '6px' }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                /* GRID VIEW - Card layout */
                <div className="item-card card" key={task.id} data-testid={`task-card-${task.id}`}>
                  <div className="item-header">
                    <h3 style={{ 
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '200px'
                    }}>{task.title}</h3>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <button 
                        className="gradient-btn" 
                        onClick={() => navigate(`/dashboard/tasks/${task.id}`)}
                        style={{ padding: '8px 16px', fontSize: '14px' }}
                      >
                        <Eye size={16} /> View
                      </button>
                      {user.role !== 'team_member' && (
                        <div style={{ display: 'flex', gap: '6px', marginLeft: '4px' }}>
                          <button 
                            onClick={() => handleEditTask(task.id)}
                            title="Edit"
                            className="icon-btn edit"
                          >
                            <Edit size={16} />
                          </button>
                          <button 
                            onClick={() => handleDeleteTask(task)}
                            title="Delete"
                            className="icon-btn delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="item-description" style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>{getPlainTextPreview(task.description, 150)}</p>
                  <div className="item-footer">
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span className="assigned-to" style={{ fontSize: '13px' }}>
                        👤 {getUserName(task.assigned_to)?.length > 20 ? getUserName(task.assigned_to).substring(0, 20) + '...' : getUserName(task.assigned_to)}
                      </span>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: getPriorityColor(task.priority) + '20',
                        color: getPriorityColor(task.priority)
                      }}>
                        {getPriorityLabel(task.priority)}
                      </span>
                    </div>
                    <span className={`status-badge ${task.status}`}>{task.status}</span>
                  </div>
                  {task.deadline && (
                    <div style={{ marginTop: '12px' }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: getDeadlineStatus(task.deadline, task.status).color + '20',
                        color: getDeadlineStatus(task.deadline, task.status).color
                      }}>
                        {getDeadlineStatus(task.deadline, task.status).icon} {formatDeadlineDate(task.deadline)}
                      </span>
                    </div>
                  )}
                </div>
              )
            ))}
          </div>
        </>
      )}

      {tasks.length === 0 && !loading && (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: '#666' }}>No tasks available</p>
        </div>
      )}

      {/* Pagination Controls */}
      {!loading && renderPagination()}
    </div>
  );
};

export default TaskManagement;